var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Avatar, Box, Button, IconButton, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Paper, TextField, Typography, } from "@mui/material";
import { blue, deepPurple, green, grey, lightGreen } from "@mui/material/colors";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { ChannelIcon } from "@sequentech/ui-essentials";
import { EMessageChannel } from "@/types/messaging";
import { ReportResult } from "./ReportResult";
import { contentPreview, demoMessageText, conversations, fakeReasonText, formatTime, parameterText, templateLabel, } from "./messageContent";
// Our own colours, after each app's look; never the apps' logos.
var THEMES = (_a = {},
    _a[EMessageChannel.WHATSAPP] = {
        header: green[800],
        headerText: "#fff",
        screen: grey[100],
        bubble: lightGreen[100],
        bubbleText: grey[900],
    },
    _a[EMessageChannel.VIBER] = {
        header: deepPurple[600],
        headerText: "#fff",
        screen: deepPurple[50],
        bubble: deepPurple[100],
        bubbleText: grey[900],
    },
    _a[EMessageChannel.MESSENGER] = {
        header: blue[700],
        headerText: "#fff",
        screen: "#fff",
        bubble: blue[800],
        bubbleText: "#fff",
    },
    _a[EMessageChannel.SMS] = {
        header: grey[200],
        headerText: grey[900],
        screen: "#fff",
        bubble: grey[200],
        bubbleText: grey[900],
    },
    _a);
var PhoneFrame = function (_a) {
    var label = _a.label, theme = _a.theme, children = _a.children;
    return (_jsx(Box, { component: "section", "aria-label": label, sx: {
            // Full width on phones; a phone of its own on larger screens.
            width: { xs: "100%", sm: 380 },
            height: { xs: "auto", sm: 680 },
            minHeight: { xs: 480, sm: 0 },
            flexShrink: 0,
            border: { xs: "1px solid ".concat(grey[300]), sm: "10px solid ".concat(grey[900]) },
            borderRadius: { xs: 1, sm: "36px" },
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            bgcolor: theme.screen,
        }, children: children }));
};
var CopyCodeButton = function (_a) {
    var code = _a.code;
    var t = useTranslation().t;
    var _b = useState("IDLE"), copied = _b[0], setCopied = _b[1];
    var copy = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, navigator.clipboard.writeText(code)];
                case 1:
                    _b.sent();
                    setCopied("COPIED");
                    return [3 /*break*/, 3];
                case 2:
                    _a = _b.sent();
                    setCopied("FAILED");
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(Box, { sx: {
            display: "flex",
            flexDirection: "column",
            mt: 1,
            mx: -1.5,
            mb: -1,
            borderTop: "1px solid",
            borderColor: lightGreen[300],
            color: green[900],
        }, children: [_jsx(Button, { fullWidth: true, size: "small", variant: "text", color: "inherit", startIcon: _jsx(ContentCopyIcon, {}), onClick: copy, sx: {
                    "bgcolor": "transparent",
                    "color": green[900],
                    "border": 0,
                    "minHeight": 40,
                    "borderRadius": 0,
                    "textTransform": "none",
                    "&:hover": {
                        bgcolor: lightGreen[200],
                        color: green[900],
                        border: 0,
                        boxShadow: "none",
                    },
                    "&:focus": { bgcolor: "transparent", color: green[900], border: 0 },
                    "&:focus-visible": { outline: "2px solid ".concat(green[900]), outlineOffset: -3 },
                }, children: t("sentMessages.content.copyCode") }), copied === "IDLE" ? null : (_jsx(Typography, { variant: "caption", role: "status", sx: { px: 1.5, pb: 1 }, children: copied === "COPIED"
                    ? t("sentMessages.content.copied")
                    : t("sentMessages.content.copyFailed") }))] }));
};
var TemplateBody = function (_a) {
    var _b;
    var content = _a.content, channel = _a.channel;
    var t = useTranslation().t;
    var messageText = demoMessageText(content, t);
    return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.5 }, children: [messageText ? (_jsx(Typography, { variant: "body2", sx: { whiteSpace: "pre-wrap" }, children: content.code && messageText.startsWith(content.code) ? (_jsxs(_Fragment, { children: [_jsx(Box, { component: "strong", children: content.code }), messageText.slice(content.code.length)] })) : (messageText) })) : (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "caption", sx: { opacity: 0.8 }, children: templateLabel(content, t) }), content.code ? (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "caption", children: t("sentMessages.content.code") }), _jsx(Typography, { sx: {
                                    fontFamily: "monospace",
                                    fontSize: "1.5rem",
                                    letterSpacing: "0.15em",
                                }, children: content.code })] })) : (((_b = content.parameters) !== null && _b !== void 0 ? _b : []).map(function (parameter, index) { return (_jsx(Typography, { variant: "body2", sx: { whiteSpace: "pre-wrap" }, children: parameterText(parameter) }, index)); }))] })), content.code && channel === EMessageChannel.WHATSAPP ? (_jsx(CopyCodeButton, { code: content.code })) : null] }));
};
var ChatBubble = function (_a) {
    var message = _a.message, channel = _a.channel, theme = _a.theme;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var content = message.content;
    return (_jsxs(Box, { component: "li", sx: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25 }, children: [_jsx(Box, { sx: {
                    minWidth: content.template_name ? "85%" : undefined,
                    maxWidth: "92%",
                    overflow: "hidden",
                    bgcolor: theme.bubble,
                    color: theme.bubbleText,
                    borderRadius: channel === EMessageChannel.MESSENGER ? "18px" : "10px",
                    px: 1.5,
                    py: 1,
                    overflowWrap: "anywhere",
                }, children: content.template_name ? (_jsx(TemplateBody, { content: content, channel: channel })) : (_jsx(Typography, { variant: "body2", sx: { whiteSpace: "pre-wrap" }, children: contentPreview(message, t) })) }), content.truncated ? (_jsx(Typography, { variant: "caption", color: "text.secondary", children: t("sentMessages.content.truncated") })) : null, _jsxs(Box, { sx: { display: "flex", gap: 1, alignItems: "center" }, children: [_jsx(Typography, { variant: "caption", color: "text.secondary", children: formatTime(message.received_at, i18n.language) }), _jsx(ReportResult, { report: message.report })] })] }));
};
var ChatHeader = function (_a) {
    var theme = _a.theme, onBack = _a.onBack, children = _a.children;
    var t = useTranslation().t;
    return (_jsxs(Box, { sx: {
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1,
            minHeight: 56,
            bgcolor: theme.header,
            color: theme.headerText,
        }, children: [onBack ? (_jsx(IconButton, { "aria-label": t("sentMessages.chat.back"), onClick: onBack, sx: { color: "inherit" }, size: "small", children: _jsx(ArrowBackIcon, {}) })) : null, children] }));
};
var ConversationList = function (_a) {
    var chats = _a.chats, onOpen = _a.onOpen;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    return (_jsx(List, { disablePadding: true, sx: { overflowY: "auto", flex: 1, bgcolor: "#fff" }, children: chats.map(function (chat) { return (_jsx(ListItem, { disablePadding: true, divider: true, children: _jsxs(ListItemButton, { "aria-label": t("sentMessages.chat.open", {
                    recipient: chat.recipient,
                    sender: chat.sender,
                }), onClick: function () { return onOpen(chat.key); }, children: [_jsx(ListItemAvatar, { children: _jsx(Avatar, { children: _jsx(PersonOutlineIcon, {}) }) }), _jsx(ListItemText, { primary: chat.recipient, secondary: _jsxs(_Fragment, { children: [_jsx(Box, { component: "span", sx: { display: "block" }, children: t("sentMessages.chat.from", { sender: chat.sender }) }), contentPreview(chat.latest, t)] }), slotProps: { secondary: { noWrap: true } } }), _jsx(Typography, { variant: "caption", color: "text.secondary", sx: { ml: 1, alignSelf: "flex-start", whiteSpace: "nowrap" }, children: formatTime(chat.latest.received_at, i18n.language) })] }) }, chat.key)); }) }));
};
var GetStartedBox = function (_a) {
    var onGetStarted = _a.onGetStarted;
    var t = useTranslation().t;
    var _b = useState(""), link = _b[0], setLink = _b[1];
    var _c = useState(false), busy = _c[0], setBusy = _c[1];
    var _d = useState(null), answer = _d[0], setAnswer = _d[1];
    var submit = function (event) { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    event.preventDefault();
                    setBusy(true);
                    setAnswer(null);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, , 3, 4]);
                    _a = setAnswer;
                    return [4 /*yield*/, onGetStarted(link.trim())];
                case 2:
                    _a.apply(void 0, [_b.sent()]);
                    return [3 /*break*/, 4];
                case 3:
                    setBusy(false);
                    return [7 /*endfinally*/];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var reason = fakeReasonText(t, answer === null || answer === void 0 ? void 0 : answer.reason);
    return (_jsxs(Paper, { variant: "outlined", component: "form", "aria-labelledby": "sent-messages-get-started", onSubmit: submit, sx: {
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            flex: 1,
            minWidth: 260,
            maxWidth: 480,
        }, children: [_jsx(Typography, { id: "sent-messages-get-started", variant: "subtitle1", component: "h3", children: t("sentMessages.getStarted.title") }), _jsx(Typography, { variant: "body2", color: "text.secondary", children: t("sentMessages.getStarted.help") }), _jsx(TextField, { size: "small", label: t("sentMessages.getStarted.link"), placeholder: "https://m.me/\u2026?ref=\u2026", value: link, onChange: function (event) { return setLink(event.target.value); } }), _jsx(Button, { type: "submit", variant: "contained", disabled: busy || !link.trim(), sx: { alignSelf: "flex-start" }, children: t("sentMessages.getStarted.submit") }), answer ? (_jsxs(Alert, { role: "status", severity: answer.status === 200
                    ? "success"
                    : answer.status != null
                        ? "warning"
                        : "error", children: [answer.status === 200
                        ? t("sentMessages.getStarted.accepted")
                        : answer.status != null
                            ? t("sentMessages.getStarted.answered", { status: answer.status })
                            : t("sentMessages.getStarted.failed", { reason: reason }), answer.status != null && answer.status !== 200 && answer.reason
                        ? " ".concat(reason)
                        : null] })) : null] }));
};
/**
 * A channel's messages as its app shows them on a phone: the chats, one per
 * sender and recipient, and a chat's messages with each one's delivery report.
 */
export var ChannelChat = function (_a) {
    var channel = _a.channel, messages = _a.messages, onGetStarted = _a.onGetStarted;
    var t = useTranslation().t;
    var theme = THEMES[channel];
    var channelName = t("messaging.channel.".concat(channel));
    var chats = conversations(messages);
    var _b = useState(null), selected = _b[0], setSelected = _b[1];
    // A chat that is gone (the fake's list restarted) closes.
    var open = chats.find(function (chat) { return chat.key === selected; });
    return (_jsxs(Box, { sx: { display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start" }, children: [_jsx(PhoneFrame, { label: t("sentMessages.chat.phone", { channel: channelName }), theme: theme, children: open ? (_jsxs(_Fragment, { children: [_jsx(ChatHeader, { theme: theme, onBack: function () { return setSelected(null); }, children: _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { variant: "subtitle2", component: "h3", noWrap: true, children: open.recipient }), _jsx(Typography, { variant: "caption", noWrap: true, component: "p", children: t("sentMessages.chat.from", { sender: open.sender }) })] }) }), _jsx(Box, { component: "ol", "aria-label": t("sentMessages.chat.messages", {
                                recipient: open.recipient,
                            }), sx: {
                                listStyle: "none",
                                m: 0,
                                p: 1.5,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                overflowY: "auto",
                                flex: 1,
                            }, children: open.messages.map(function (message) { return (_jsx(ChatBubble, { message: message, channel: channel, theme: theme }, message.id)); }) })] })) : (_jsxs(_Fragment, { children: [_jsxs(ChatHeader, { theme: theme, children: [_jsx(ChannelIcon, { channel: channel }), _jsx(Typography, { variant: "subtitle1", component: "h3", children: t("sentMessages.chat.chats", { channel: channelName }) })] }), chats.length === 0 ? (_jsx(Typography, { color: "text.secondary", sx: { p: 2 }, children: t("sentMessages.emptyChannel", { channel: channelName }) })) : (_jsx(ConversationList, { chats: chats, onOpen: setSelected }))] })) }), channel === EMessageChannel.MESSENGER && onGetStarted ? (_jsx(GetStartedBox, { onGetStarted: onGetStarted })) : null] }));
};
